import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { ArrowLeft } from 'lucide-react';

export default function TermsPage() {
  return (
    <div className="min-h-screen bg-stone-50">
      <Helmet>
        <title>Terms of Service — Practicio</title>
        <meta name="description" content="Practicio's Terms of Service. Read the terms and conditions governing the use of our practice management platform for architects and interior designers." />
        <link rel="canonical" href="https://practicio.io/terms" />
      </Helmet>
      <Header />
      <main className="pt-28 pb-20">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-stone-500 hover:text-stone-900 transition-colors mb-10" data-testid="terms-back-link">
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </Link>

          <h1 className="text-3xl sm:text-4xl font-bold text-slate-700 mb-2" data-testid="terms-heading">Terms of Service</h1>
          <p className="text-sm text-stone-400 mb-10">Last updated: February 2026</p>

          <div className="space-y-8 text-stone-600 leading-relaxed">
            <section className="space-y-3">
              <h2 className="text-base md:text-lg font-semibold text-stone-900">1. Acceptance of Terms</h2>
              <p>
                By accessing or using Practicio's platform and services, you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our services. These terms apply to all users, including visitors, registered users, and subscribers.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-base md:text-lg font-semibold text-stone-900">2. Description of Service</h2>
              <p>
                Practicio provides a practice management platform designed for architects and interior designers. Our services include client approval tracking, BOQ management, scope change logging, billing milestone management, and related features as described on our website.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-base md:text-lg font-semibold text-stone-900">3. Account Registration</h2>
              <p>To use our platform, you must create an account by providing accurate and complete information. You are responsible for:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Maintaining the security of your account credentials</li>
                <li>All activity that occurs under your account</li>
                <li>Notifying us immediately of any unauthorised access</li>
                <li>Keeping your account information up to date</li>
              </ul>
            </section>

            <section className="space-y-3">
              <h2 className="text-base md:text-lg font-semibold text-stone-900">4. Acceptable Use</h2>
              <p>You agree not to:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Use the service for any unlawful purpose or in violation of any applicable laws</li>
                <li>Attempt to gain unauthorised access to any part of the platform</li>
                <li>Interfere with or disrupt the integrity or performance of the service</li>
                <li>Upload or transmit viruses, malware, or other harmful code</li>
                <li>Use the platform to store or transmit content that infringes on intellectual property rights</li>
                <li>Resell, sublicence, or redistribute the service without our written consent</li>
              </ul>
            </section>

            <section className="space-y-3">
              <h2 className="text-base md:text-lg font-semibold text-stone-900">5. Your Data</h2>
              <p>
                You retain all ownership rights to the data you enter into Practicio. We do not claim ownership of your project data, client information, or any other content you create on the platform. You grant us a limited licence to use your data solely for the purpose of providing and improving our services.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-base md:text-lg font-semibold text-stone-900">6. Subscription and Payments</h2>
              <p>
                Paid plans are billed on a monthly or annual basis as selected at the time of subscription. All fees are quoted in Indian Rupees (INR) and are exclusive of applicable taxes. Payments are non-refundable except as specified in our 14-day money-back guarantee for new subscriptions.
              </p>
              <p>
                We reserve the right to change pricing with 30 days' advance notice. Price changes will apply at the start of your next billing cycle.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-base md:text-lg font-semibold text-stone-900">7. Service Availability</h2>
              <p>
                We strive to maintain high availability of our platform but do not guarantee uninterrupted access. We may perform scheduled maintenance with advance notice. We are not liable for temporary unavailability due to factors beyond our reasonable control.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-base md:text-lg font-semibold text-stone-900">8. Limitation of Liability</h2>
              <p>
                To the maximum extent permitted by law, Practicio shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the service. Our total liability for any claim shall not exceed the amount you paid us in the 12 months preceding the claim.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-base md:text-lg font-semibold text-stone-900">9. Termination</h2>
              <p>
                You may cancel your subscription at any time through your account settings. We may suspend or terminate your access if you violate these terms. Upon termination, your right to access the platform ceases, and we will retain your data for 90 days before deletion unless otherwise required by law.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-base md:text-lg font-semibold text-stone-900">10. Governing Law</h2>
              <p>
                These terms are governed by the laws of India. Any disputes arising from these terms or your use of Practicio shall be subject to the exclusive jurisdiction of the courts in Mumbai, India.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-base md:text-lg font-semibold text-stone-900">11. Changes to Terms</h2>
              <p>
                We may update these Terms of Service from time to time. Material changes will be communicated via email or a prominent notice on our website. Your continued use of the service after changes take effect constitutes acceptance of the revised terms.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-base md:text-lg font-semibold text-stone-900">12. Contact</h2>
              <p>
                For questions about these Terms of Service, contact us at{' '}
                <a href="mailto:hello@practicio.io" className="text-slate-600 hover:text-slate-700 font-medium underline underline-offset-2">
                  hello@practicio.io
                </a>
              </p>
            </section>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
